import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { GET, POST, httpOk } from 'helpers/api'
import { formatMoney } from 'helpers/helpers'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  HAMKORGA ZAKAZ
 *  2026-08-24
 *
 *  Egasining talabi: dastur hamkorga O'ZI hech narsa yubormaydi — dastur
 *  TAVSIYA beradi («bu kamar tugayapti»), zakazni OMBORCHI yozadi, «Юбориш»
 *  bosilganda zakaz hamkor bilan umumiy TELEGRAM GURUHIGA tushadi.
 *
 *  Guruh bog'lash (2026-08-28 dan): hamkor zakazlari uchun ALOHIDA bot bor
 *  (Созламалар → Telegram → «Ҳамкор заказ боти»). Token kiritilgach, o'sha bot
 *  hamkor guruhiga qo'shiladi — guruh AVTOMAT bog'lanadi (yoki guruhda «/zakaz»).
 *
 *  ⚠️ «Қабул қилинди» OMBORGA YOZMAYDI — tovar odatdagi prixod/
 *     inventarizatsiya orqali kiradi (egasi: «faktga to'g'rilab kiritamiz»).
 * ═══════════════════════════════════════════════════════════════════════════
 */

const STATUS = {
	1:  { label: 'Тайёрланмоқда', color: '#a16207', bg: 'var(--cat-wr-bg, #fff3cd)' },
	2:  { label: 'Юборилган',     color: '#0d6efd', bg: 'var(--cat-info-bg, #e7f1ff)' },
	3:  { label: 'Қабул қилинган', color: 'var(--ok-mid, #198754)', bg: 'var(--ok-mid-bg, #e8f5e9)' },
	'-1': { label: 'Бекор',       color: 'var(--dk-text-2, #6c757d)', bg: 'var(--an-chip-bg, #f1f3f7)' },
}

const son = v => {
	const n = Number(v) || 0
	return Number.isInteger(n) ? formatMoney(n, 0) : formatMoney(Math.round(n * 10) / 10, 1)
}

export default function SupplierOrders() {
	const { t } = useTranslation()

	const [rows, setRows] = useState([])
	const [tg, setTg] = useState({ bound: false, group: null })
	const [loading, setLoading] = useState(false)
	const [busy, setBusy] = useState(0)          // amaldagi zakaz id

	// Yangi zakaz modali
	const [show, setShow] = useState(false)
	const [note, setNote] = useState('')
	const [lines, setLines] = useState([])       // {product_id, name, unit_name, qty, checked, balance, cover_days}
	const [search, setSearch] = useState('')
	const [found, setFound] = useState([])
	const [saving, setSaving] = useState(false)

	const load = useCallback(async () => {
		setLoading(true)
		const res = await GET('/supplier-orders', {}, { loader: false })
		setLoading(false)
		if (httpOk(res)) {
			setRows(res.data.data || [])
			setTg(res.data.telegram || { bound: false })
		}
	}, [])

	useEffect(() => { load() }, [load])

	async function openModal() {
		setNote('')
		setSearch('')
		setFound([])
		setShow(true)
		// Tavsiya — «Тайёр маҳсулот заҳираси» bilan bitta formuladan
		const res = await GET('/supplier-orders/suggest', {}, { loader: false })
		if (httpOk(res)) {
			setLines((res.data.data || []).map(s => ({
				product_id: s.product_id, name: s.name, unit_name: s.unit_name,
				qty: s.suggested, checked: true,
				balance: s.balance, cover_days: s.cover_days, status: s.status,
			})))
		}
	}

	/* Tavsiyada yo'q mahsulotni qo'shish — zapas ekranidagi ro'yxatdan qidirib */
	async function doSearch(q) {
		setSearch(q)
		if (!q.trim()) { setFound([]); return }
		const res = await GET('/stock/levels', { search: q, size: 8 }, { loader: false })
		if (httpOk(res)) setFound(res.data.data || [])
	}
	function addFound(f) {
		if (lines.some(l => l.product_id === f.id)) { setSearch(''); setFound([]); return }
		setLines([...lines, {
			product_id: f.id, name: f.name, unit_name: f.unit_name,
			qty: f.suggested_order || '', checked: true,
			balance: f.balance, cover_days: f.cover_days, status: f.status,
		}])
		setSearch(''); setFound([])
	}

	async function save() {
		const items = lines
			.filter(l => l.checked && Number(l.qty) > 0)
			.map(l => ({ product_id: l.product_id, quantity: Number(l.qty) }))
		if (!items.length) { toast.error('Kamida bitta mahsulot va soni kerak'); return }
		setSaving(true)
		const res = await POST('/supplier-orders', { note: note || null, items }, { loader: false })
		setSaving(false)
		if (httpOk(res)) {
			toast.success(t('saved'))
			setShow(false)
			load()
		}
	}

	async function act(id, action, confirmText) {
		if (confirmText && !window.confirm(confirmText)) return
		setBusy(id)
		const res = await POST(`/supplier-orders/${id}/${action}`, {}, { loader: false })
		setBusy(0)
		if (httpOk(res)) {
			toast.success(res.data?.message || 'ok')
			load()
		}
	}

	return (
		<div className="an-page">
			<div className="page-header d-flex justify-content-between align-items-center mb-3">
				<h4 className="mb-0">{t('supplier_orders')}</h4>
				<button className="btn btn-primary" onClick={openModal}>
					<i className="uil-plus me-1" />Янги заказ
				</button>
			</div>

			{/* Guruh holati — yubora olmasligini omborchi OLDINDAN bilsin */}
			{!tg.bound &&
				<div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
					<i className="uil-exclamation-triangle me-1" />
					Ҳамкор Telegram гуруҳи ҳали боғланмаган. <b>Созламалар → Telegram → «Ҳамкор
					заказ боти»</b> да ботнинг токенини киритинг, сўнг <b>ўша ботни</b> ҳамкор
					гуруҳига қўшинг — гуруҳ ўзи боғланади.
				</div>
			}
			{tg.bound &&
				<div className="text-muted mb-2" style={{ fontSize: 12 }}>
					<i className="uil-telegram me-1" style={{ color: '#0d6efd' }} />
					Гуруҳ: <b>{tg.group || 'боғланган'}</b>
				</div>
			}

			<div className="card">
				<div className="card-body p-0">
					<div className="table-responsive">
						<table className="table table-hover mb-0 align-middle">
							<thead>
								<tr>
									<th style={{ width: 70 }}>№</th>
									<th>Маҳсулотлар</th>
									<th style={{ width: 120 }}>{t('date')}</th>
									<th style={{ width: 130 }}>Ким ёзди</th>
									<th style={{ width: 130 }} className="text-center">{t('status')}</th>
									<th style={{ width: 210 }} className="text-center">{t('actions')}</th>
								</tr>
							</thead>
							<tbody>
								{rows.length === 0 &&
									<tr><td colSpan={6} className="text-center text-muted py-4">
										{loading ? t('loading') : t('list_empty')}
									</td></tr>}
								{rows.map(o => {
									const st = STATUS[o.status] || STATUS['-1']
									return (
										<tr key={o.id}>
											<td className="fw-bold">#{o.id}</td>
											<td>
												{(o.items || []).map((it, i) => (
													<div key={i} style={{ fontSize: 13 }}>
														{it.product_name} — <b>{son(it.quantity)}</b>
														<span className="text-muted"> {it.unit_name || ''}</span>
													</div>
												))}
												{o.note && <div className="text-muted" style={{ fontSize: 11.5 }}>💬 {o.note}</div>}
												{o.send_error &&
													<div style={{ fontSize: 11.5, color: 'var(--warn-ink, #dc3545)' }}>
														Юборилмади: {o.send_error}
													</div>}
											</td>
											<td className="text-muted" style={{ fontSize: 12 }}>
												{String(o.created_at).slice(0, 16).replace('T', ' ')}
												{o.sent_at && <div>юб: {String(o.sent_at).slice(5, 16).replace('T', ' ')}</div>}
											</td>
											<td className="text-muted" style={{ fontSize: 12 }}>{o.created_by}</td>
											<td className="text-center">
												<span className="d-inline-block px-2 py-1 rounded"
													style={{ background: st.bg, color: st.color, fontSize: 11, fontWeight: 700 }}>
													{st.label}
												</span>
											</td>
											<td className="text-center">
												{o.status === 1 && <>
													<button className="btn btn-sm btn-primary me-1"
														disabled={busy === o.id}
														title={tg.bound ? 'Гуруҳга юбориш' : 'Аввал ҳамкор ботини созланг ва гуруҳга қўшинг'}
														onClick={() => act(o.id, 'send')}>
														<i className="uil-telegram" /> Юбориш
													</button>
													<button className="btn btn-sm btn-outline-danger"
														disabled={busy === o.id}
														onClick={() => act(o.id, 'cancel', `Заказ #${o.id} бекор қилинсинми?`)}>
														<i className="uil-times" />
													</button>
												</>}
												{o.status === 2 && <>
													<button className="btn btn-sm btn-outline-success me-1"
														disabled={busy === o.id}
														title="Ҳамкор келтирди (омборга ёзмайди — приход алоҳида)"
														onClick={() => act(o.id, 'receive')}>
														<i className="uil-check" /> Қабул қилинди
													</button>
													<button className="btn btn-sm btn-outline-danger"
														disabled={busy === o.id}
														onClick={() => act(o.id, 'cancel', `Юборилган заказ #${o.id} бекор қилинсинми? Ҳамкорга гуруҳда ўзингиз ёзинг.`)}>
														<i className="uil-times" />
													</button>
												</>}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* ═══ Yangi zakaz ═══ */}
			{show &&
				<>
					<div className="modal-backdrop fade show" onClick={() => setShow(false)} />
					<div className="modal fade show d-block" tabIndex="-1">
						<div className="modal-dialog modal-lg modal-dialog-centered">
							<div className="modal-content">
								<div className="modal-header">
									<h6 className="modal-title">Янги заказ — ҳамкорга</h6>
									<button type="button" className="btn-close" onClick={() => setShow(false)} />
								</div>
								<div className="modal-body">
									{lines.length === 0 &&
										<div className="alert alert-light border py-2" style={{ fontSize: 12.5 }}>
											Ҳозирча кам қолган назоратдаги маҳсулот йўқ. Пастдаги қидирувдан
											қўшишингиз мумкин.
										</div>}

									{lines.length > 0 &&
										<table className="table table-sm align-middle mb-3">
											<thead>
												<tr>
													<th style={{ width: 30 }}></th>
													<th>{t('product_name')}</th>
													<th style={{ width: 110 }} className="text-end">{t('balance2')}</th>
													<th style={{ width: 90 }} className="text-end">Неча кунга</th>
													<th style={{ width: 120 }}>Сони</th>
												</tr>
											</thead>
											<tbody>
												{lines.map((l, i) => (
													<tr key={l.product_id} style={{ opacity: l.checked ? 1 : .45 }}>
														<td>
															<input type="checkbox" className="form-check-input"
																checked={l.checked}
																onChange={e => {
																	const next = [...lines]; next[i] = { ...l, checked: e.target.checked }; setLines(next)
																}} />
														</td>
														<td style={{ fontSize: 13 }}>
															{l.name}
															<span className="text-muted ms-1" style={{ fontSize: 11 }}>{l.unit_name}</span>
														</td>
														<td className="text-end" style={{ fontSize: 12.5 }}>{son(l.balance)}</td>
														<td className="text-end" style={{ fontSize: 12.5 }}>
															{l.cover_days === null ? '—' : `${l.cover_days} кун`}
														</td>
														<td>
															<input type="number" className="form-control form-control-sm" min="0"
																value={l.qty}
																onChange={e => {
																	const next = [...lines]; next[i] = { ...l, qty: e.target.value }; setLines(next)
																}} />
														</td>
													</tr>
												))}
											</tbody>
										</table>}

									{/* Tavsiyada bo'lmagan mahsulotni qo'shish */}
									<div className="position-relative mb-3">
										<input type="text" className="form-control form-control-sm"
											placeholder="Бошқа маҳсулот қўшиш — номи бўйича қидиринг..."
											value={search} onChange={e => doSearch(e.target.value)} />
										{found.length > 0 &&
											<div className="position-absolute w-100 border rounded mt-1"
												style={{ background: 'var(--dk-surface, #fff)', zIndex: 10, maxHeight: 220, overflowY: 'auto' }}>
												{found.map(f => (
													<button key={f.id} type="button"
														className="d-block w-100 text-start border-0 px-2 py-1"
														style={{ background: 'transparent', fontSize: 12.5 }}
														onClick={() => addFound(f)}>
														{f.name} <span className="text-muted">({son(f.balance)} {f.unit_name || ''})</span>
													</button>
												))}
											</div>}
									</div>

									<div className="form-group">
										<label style={{ fontSize: 12.5 }}>Изоҳ (ихтиёрий — гуруҳга ҳам тушади)</label>
										<textarea className="form-control" rows="2" value={note}
											onChange={e => setNote(e.target.value)} />
									</div>
								</div>
								<div className="modal-footer">
									<button className="btn btn-outline-warning" onClick={() => setShow(false)}>{t('cancel')}</button>
									<button className="btn btn-primary" onClick={save} disabled={saving}>
										{saving ? t('loading') : t('save')}
									</button>
								</div>
							</div>
						</div>
					</div>
				</>
			}
		</div>
	)
}
